import React, { useEffect, useRef } from 'react'
import { useAuthContext } from './reducer'
import styled from '@emotion/styled'
import { gsColors } from '../../styles/Variables'
import Error from './Error'
import { segmentEvent, mq, resendCooldownKey, useResendCooldown } from '../../helpers'

// OTP boxes match Figma node 659:183 — individual rounded boxes with a 2px border,
// light grey by default and cobalt on the focused box. Figma desktop values are
// 96x120/radius 12; scaled down here to fit the app's narrower auth column while
// preserving the design's rounded look and cobalt focus ring.
const OTPWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: max-content;

  .otp-inputs-container {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin: 45px 0 16px;
    position: relative;

    /*
     * The field that actually holds the code. It covers the six boxes at full size and is
     * made invisible with transparent text — NOT with display:none, opacity:0 or a height of
     * 0, because iOS Safari only offers the "From Messages" one-time-code suggestion for a
     * field it considers rendered. pointer-events:none lets taps fall through to the boxes,
     * which keep their own click handling.
     */
    .otp-real-input {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0;
      border: none;
      background-color: transparent;
      color: transparent;
      caret-color: transparent;
      text-align: center;
      pointer-events: none;
      /* keep >= 16px: iOS zooms the page when a smaller-text field takes focus */
      font-size: 16px;
      font-family: Euclid;

      &:focus {
        outline: none;
      }
    }

    .opt-input-field {
      width: 56px;
      height: 70px;
      border: 2px solid ${gsColors.textGrey};
      text-align: center;
      font-size: 24px;
      font-family: Euclid;
      border-radius: 12px;
      cursor: pointer;
      background-color: transparent;

      ${mq('max', '768px')} {
        width: 44px;
        height: 56px;
        font-size: 20px;
      }
    }

    .opt-input-field.focused {
      border-color: ${gsColors.cobalt};
      outline: none;
    }
  }

  .error-container {
    div {
      margin: 0 auto;
      color: #C62828;
      font-size: 12px;
    }
  }
`

/*
 * Resend/help block. The resend control is muted and disabled while the 60-second cooldown
 * runs, then turns cobalt when it expires. Below it sits the route back to the identifier
 * field — necessary because resend no longer navigates backwards, so without it a mistyped
 * number is a dead end on a screen with no back button.
 */
const ResendPasscodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 45px;
  text-align: center;

  p {
    font-family: Euclid;
    font-size: 16px;
    font-weight: 400;
    color: ${gsColors.black};
    margin: 0;
  }

  button {
    background-color: transparent;
    border: none;
    color: ${gsColors.cobalt};
    font-family: Euclid;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    padding: 0;

    &:disabled {
      color: ${gsColors.darkGrey};
      cursor: default;
    }
  }

  .change-destination {
    color: ${gsColors.darkGrey};
    font-size: 14px;
    font-weight: 400;
    text-decoration: underline;
  }
`

export const OTPInput = ({ handleSubmit, handleResend = () => false, submitError }) => {
  const OTP_LENGTH = 6
  const {
    state: {
      otp,
      otpActiveIdx,
      otpChannel,
      working,
      phone,
      email,
    },
    dispatch
  } = useAuthContext()
  const hiddenInputRef = useRef(null)
  const isEmail = otpChannel === 'email'
  const destination = isEmail ? email?.value : phone?.value
  const { secondsRemaining, isCoolingDown, startCooldown } = useResendCooldown(
    resendCooldownKey(otpChannel, destination)
  )

  const handleInputChange = (e) => {
    // Remove all non-digit characters
    const value = e.target.value.replace(/\D/g, '')

    const characters = value.split('')
    const filler = Array(OTP_LENGTH - characters.length).fill('')

    // merge characters and fillers to create the new otp Array
    // the purpose of the filler is to ensure there is always OTP_LENGTH characters in the otp array
    dispatch({
      type: 'SET_OTP',
      otp: [...characters, ...filler],
      otpActiveIdx: value.length
    })
  }

  // Handle backspace key press
  const handleKeyDown = (e) => {
    if (e.key === 'Backspace') {
      // check if there is characters that can be deleted
      if (hiddenInputRef.current.value.length > 0) {
        const newOtp = otp.slice()
        // Clear the digit at the current position
        newOtp[otpActiveIdx] = ''

        dispatch({
          type: 'SET_OTP',
          otp: newOtp,
          otpActiveIdx: otpActiveIdx > 0 ? otpActiveIdx - 1 : 0
        })
      }
    }
  }

  // handle clicking on otp input field
  const handleClick = (index) => {
    hiddenInputRef.current.focus()
    // sets cursor position to the end of the input field
    hiddenInputRef.current.setSelectionRange(index, index)
    dispatch({
      type: 'SET_OTP_ACTIVE_IDX',
      otpActiveIdx: index
    })
  };

  /**
   * resendCode: request a fresh one-time code for the identifier already in state. The cooldown
   * only starts when a code actually went out, so a failed send doesn't lock the user out for
   * a minute.
   *
   * @return {Promise<void>}
   */
  const resendCode = async () => {
    segmentEvent('Resend Code', { type: otpChannel })

    if (await handleResend()) {
      startCooldown()
    }
  }

  /**
   * changeDestination: return to the identifier-entry screen so a mistyped phone number or
   * email can be corrected. RESET_OTP_INPUT toggles the screen and clears the entered digits
   * while leaving state.phone / state.email intact for editing.
   *
   * @return {void}
   */
  const changeDestination = () => {
    dispatch({ type: 'RESET_OTP_INPUT' })
  }

  useEffect(() => {
    hiddenInputRef.current.focus();
  }, []);

  /*
   * WebOTP — the Android/Chrome equivalent of autocomplete="one-time-code", which Chrome
   * does not implement. Chrome only resolves this when the SMS itself ends with a line
   * binding the code to this origin, e.g.
   *
   *   @www.glamsquad.com #123456
   *
   * Until Starbase's OTP SMS carries that line the promise simply never settles, which is
   * harmless: it is aborted on unmount. iOS/Safari ignores this and uses the input's
   * autoComplete="one-time-code" instead.
   */
  useEffect(() => {
    if (typeof window === 'undefined' || !('OTPCredential' in window)) {
      return
    }

    const abortController = new AbortController()

    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: abortController.signal })
      .then((credential) => {
        const digits = (credential?.code || '').replace(/\D/g, '').slice(0, OTP_LENGTH)
        if (digits.length !== OTP_LENGTH) {
          return
        }

        // fills every box; the effect below then auto-submits, same as a manual entry
        dispatch({ type: 'SET_OTP', otp: digits.split(''), otpActiveIdx: OTP_LENGTH })
      })
      // an abort on unmount and a declined prompt both land here and are non-events
      .catch(() => {})

    return () => abortController.abort()
  }, [dispatch]);

  useEffect(() => {
    const val = otp.join('')
    if (val.length !== OTP_LENGTH) {
      return
    }

    handleSubmit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp])

  return (
    <OTPWrapper>
      <div className='otp-inputs-container'>
        {/*
          The real input is layered over the boxes (see .otp-real-input) so the browser treats
          it as a visible field and offers the SMS code. It carries every hint the platforms
          look for: autoComplete="one-time-code" (iOS/Safari), a numeric keypad, and no
          autocorrect/autocapitalize/spellcheck.
        */}
        <input
          ref={hiddenInputRef}
          id='one-time-code'
          type="text"
          name="one-time-code"
          aria-label='One-time code'
          value={otp.join('')}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          maxLength={OTP_LENGTH}
          className='otp-real-input'
          autoComplete="one-time-code"
          inputMode="numeric"
          pattern="[0-9]*"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
        />
        {/*
          Each visible box is readOnly, NOT disabled: it stays normally styled, and clicking it
          calls handleClick to focus the real input above (which captures the actual
          typing/paste). A disabled input can't receive that click and renders greyed — so
          don't "fix" these to disabled. They're presentational duplicates of the real field,
          so they're hidden from assistive tech and skipped by tab and by autofill.
        */}
        {otp.map((value, idx) => (
          <input
            key={idx}
            type='text'
            value={value}
            readOnly
            aria-hidden='true'
            tabIndex={-1}
            autoComplete='off'
            onClick={() => handleClick(idx)}
            className={`opt-input-field ${idx === otpActiveIdx ? 'focused' : ''}`}
            maxLength={1}
          />
        ))}
      </div>
      <div className='error-container' style={{ textAlign: 'center' }}>
        <Error error={submitError} />
      </div>
      <ResendPasscodeWrapper>
        <p>
          {isEmail
            ? 'Can’t find the email? Check your spam folder.'
            : 'Didn’t get a text?'}
          {' '}
          <button type='button' onClick={resendCode} disabled={isCoolingDown || working}>
            {isCoolingDown ? `Resend code in ${secondsRemaining}s` : 'Resend code'}
          </button>
        </p>
        <button type='button' className='change-destination' onClick={changeDestination}>
          {isEmail ? 'Use a different email' : 'Use a different number'}
        </button>
      </ResendPasscodeWrapper>

    </OTPWrapper>
  );
};

